/**
 * Stripe Payment Service
 * Handles all communication with the Stripe Payment API backend
 * Supports one-time credit purchases
 */

import { getRuntimeConfig } from '../config/runtime';

// API Configuration — Stripe payments go through the Java Lambda
function getStripeApiUrl(): string {
  const runtimeConfig = getRuntimeConfig();
  return runtimeConfig?.VITE_STRIPE_API_URL ||
    (import.meta.env.VITE_STRIPE_API_URL as string) ||
    'https://ppu7ft7aqe.execute-api.ap-southeast-2.amazonaws.com/dev1';
}

// ─── Types ──────────────────────────────────────────────────────────────────

export type CreditPackageId = 'CREDITS_STARTER' | 'CREDITS_GROWTH' | 'CREDITS_ENTERPRISE' | 'CREDITS_OFFER';
// Frontend billing plan names (used in StripeBilling step)
//export type BillingPlanId = 'STARTER' | 'GROWTH' | 'ENTERPRISE' | 'OFFER';

/** Map frontend billing plan names → Lambda packageId */
export function mapPlanToPackageId(plan: string): CreditPackageId {
  const mapping: Record<string, CreditPackageId> = {
    'STARTER':    'CREDITS_STARTER',
    'GROWTH':     'CREDITS_GROWTH',
    'ENTERPRISE': 'CREDITS_ENTERPRISE',
    'OFFER':      'CREDITS_OFFER',
  };
  return mapping[plan] ?? 'CREDITS_STARTER';
}

export interface CreditPackage {
  id: CreditPackageId;
  name: string;
  description: string;
  credits: number;
  price: number;
  originalPrice: number;
  pricePerCredit: number;
  discountPercent: number;
  savings: number;
}

export interface CreatePaymentIntentRequest {
  packageId: CreditPackageId;
  customerEmail: string;
  customerName: string;
  customerId?: string;
  idempotencyKey?: string;
  metadata?: Record<string, string>;
  /** Override payment method types, e.g. ['au_becs_debit'] for bank transfer */
  paymentMethodTypes?: string[];
}

export interface CreatePaymentIntentResponse {
  clientSecret?: string;
  paymentIntentId: string;
  amount?: number;
  currency?: string;
  package?: CreditPackage;
  invoiceId?: string;
  stripeCustomerId?: string;
  reused?: boolean;
  alreadyPaid?: boolean;
  status?: string;
  message?: string;
}

export interface ConfirmPaymentResponse {
  success: boolean;
  status: string;
  credits: number;
  receiptUrl?: string;
}

export interface GetConfigResponse {
  publishableKey: string;
  packages: CreditPackage[];
}

export interface ApiError {
  code: string;
  message: string;
  statusCode: number;
}

// ─── API Functions ──────────────────────────────────────────────────────────

/**
 * Fetch Stripe publishable key and available packages from the backend
 */
export async function getStripeConfig(): Promise<GetConfigResponse> {
  const response = await fetch(`${getStripeApiUrl()}/stripe/config`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error: ApiError = await response.json();
    throw new Error(error.message || 'Failed to fetch Stripe config');
  }

  return response.json();
}

/**
 * Create a PaymentIntent on the backend
 * Returns a clientSecret to complete payment on the frontend
 */
export async function createPaymentIntent(
  request: CreatePaymentIntentRequest
): Promise<CreatePaymentIntentResponse> {

  const response = await fetch(`${getStripeApiUrl()}/stripe/create-payment-intent`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      ...request,
      ...(request.paymentMethodTypes?.length ? { payment_method_types: request.paymentMethodTypes } : {}),
    }),
  });

  if (!response.ok) {
    const rawBody = await response.text();
    let errorMessage = 'Failed to create payment intent';
    try {
      const error = JSON.parse(rawBody) as ApiError;
      errorMessage = error.message || errorMessage;
    } catch {
      errorMessage = rawBody || errorMessage;
    }
    throw new Error(errorMessage);
  }

  return response.json();
}

/**
 * Check if customer has already made a recent payment
 * This is a pre-check before showing the payment form
 */
export async function checkExistingPayment(
  customerEmail: string,
  packageId: CreditPackageId
): Promise<{
  hasPaid: boolean;
  paymentIntentId?: string;
  message?: string;
  paidAt?: string;
  invoiceId?: string;
  hasOpenInvoice?: boolean;
}> {
  try {
    const response = await fetch(`${getStripeApiUrl()}/stripe/check-payment-status`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ customerEmail, packageId }),
    });

    if (!response.ok) {
      return { hasPaid: false };
    }

    return response.json();
  } catch (error) {
    return { hasPaid: false };
  }
}

/**
 * Confirm payment status after Stripe Elements completes
 */
export async function confirmPayment(paymentIntentId: string): Promise<ConfirmPaymentResponse> {
  const response = await fetch(`${getStripeApiUrl()}/stripe/confirm-payment`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ paymentIntentId }),
  });

  if (!response.ok) {
    const error: ApiError = await response.json();
    throw new Error(error.message || 'Failed to confirm payment');
  }

  return response.json();
}

// ─── Health Check ───────────────────────────────────────────────────────────

/**
 * Health check for the API
 */
export async function checkApiHealth(): Promise<boolean> {
  try {
    const response = await fetch(`${getStripeApiUrl()}/health`, {
      method: 'GET',
    });
    return response.ok;
  } catch {
    return false;
  }
}
